import { AlertService, AudienceService, FunnelService } from '../../_services';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { CrudSavePopupComponent } from '../../_directives';
import { Audience, AudiencePopup, AudienceType, Pager } from '../../_models';
import { config } from '../../../config/config';

@Component({
  moduleId: module.id.toString(),
  selector: 'segment-crud-popup',
  templateUrl: './segment-crud-popup.component.html',
  styleUrls: ['../retention/retention-offers.component.scss'],
})
export class SegmentCrudPopupComponent extends CrudSavePopupComponent implements OnInit {
  ngModelOptions = { standalone: true };
  audiencePopup = AudiencePopup;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected formBuilder: FormBuilder,
    protected audienceService: AudienceService,
    protected alertService: AlertService,
    protected funnelService: FunnelService
  ) {
    super(router, location, route, audienceService, alertService);
    this.objectName = 'Segment';
    this.isNew = false;
    this.isPartial = true;
  }

  audiences: Audience[] = [];
  audiencesLoading = false;
  // Retention-segment create (select-or-create audience). Survey-response create keeps the plain name input.
  isSegmentCreate = false;

  ngOnInit() {
    this.isSegmentCreate =
      this.inputData.popupType === AudiencePopup.Create && !this.inputData.is_survey_response;

    this.setForm(
      this.formBuilder.group(
        this.isSegmentCreate
          ? { audience: [null, Validators.required] }
          : { name: [this.inputData.name || '', Validators.required] }
      )
    );

    if (this.isSegmentCreate) {
      this.fetchAudiences();
    }

    super.ngOnInit();
  }

  fetchAudiences() {
    this.audiencesLoading = true;

    this.audienceService.list({ page: 1, page_size: config.maxPageSize, type: AudienceType.Retention }).subscribe(
      (data: Pager) => {
        this.audiences = data.results;
        this.audiencesLoading = false;
      },
      (error) => {
        this.audiencesLoading = false;
        this.handleSubmitError(error);
      }
    );
  }

  // ng-select addTag: a typed-in value becomes a new audience (no id) rather than being persisted here
  addAudienceTag = (name: string) => ({ name: (name || '').trim() });

  get isExistingAudienceSelected(): boolean {
    const value = this.form && this.form.get('audience') && this.form.get('audience').value;
    return !!(value && value.id);
  }

  onSubmit() {
    const { popupType, name, audience_id, offer_id, segment_funnel_id, ...formData } = this.getFormData();

    if (popupType === AudiencePopup.Conditions) {
      this.onSave.emit({ editConditions: true, id: audience_id });
    } else if (popupType === AudiencePopup.Create) {
      this.submitted = true;

      if (this.form.valid) {
        // Survey-response create keeps the original single-name flow (name text input)
        if (!this.isSegmentCreate) {
          this.loading = true;

          this.audienceService.validateName(name).subscribe(
            () => {
              this.loading = false;
              this.onSave.emit({ name, offer_id });
            },
            (error) => {
              this.handleSubmitError(error);
              this.loading = false;
            }
          );
          return;
        }

        const selectedAudience = this.form.value.audience;

        // Existing audience selected -> create the segment directly, WITHOUT name validation
        // and without opening the conditions popup.
        if (selectedAudience && selectedAudience.id) {
          this.onSave.emit({ audienceId: selectedAudience.id });
          return;
        }

        // New audience name -> validate it, then hand the name off to the conditions popup (existing flow)
        this.loading = true;

        this.audienceService.validateName(selectedAudience.name).subscribe(
          () => {
            this.loading = false;
            this.onSave.emit({ name: selectedAudience.name, offer_id });
          },
          (error) => {
            this.handleSubmitError(error);
            this.loading = false;
          }
        );
      }
    } else {
      this.submitted = true;
      if (
        this.form.valid ||
        this.inputData.popupType === AudiencePopup.Delete ||
        this.inputData.popupType === AudiencePopup.Duplicate
      ) {
        this.loading = true;

        let operation: Observable<Object>;
        let operationTitle = '';

        if (popupType === AudiencePopup.Duplicate) {
          operationTitle = 'Duplicated';
          operation = this.funnelService.clone(segment_funnel_id);
        } else if (popupType === AudiencePopup.Update) {
          operationTitle = 'Updated';
          operation = this.audienceService.update(audience_id, { name, ...formData });
        } else if (popupType === AudiencePopup.Delete) {
          operationTitle = 'Deleted';
          operation = this.funnelService.delete(segment_funnel_id);
        }

        operation.subscribe(
          (data) => {
            if (this.showSuccessMessage) {
              this.alertService.success(`${this.objectName} ${operationTitle} Successfully.`, true);
            }

            this.canDeactivateService.setFormDirty(false);
            this.onSaveComplete(data);
          },
          (error) => {
            this.handleSubmitError(error);
            this.loading = false;
          }
        );
      }
    }
  }

  protected getFormData() {
    const data = {
      ...this.form.value,
      audience_id: this.inputData.audience_id,
      popupType: this.inputData.popupType,
      offer_id: this.inputData.offer_id,
      segment_funnel_id: this.inputData.segment_funnel_id,
    };
    return data;
  }

  popupTypeTextMap: Partial<Record<AudiencePopup, string>> = {
    [AudiencePopup.Create]: 'Next',
    [AudiencePopup.Delete]: 'Delete',
    [AudiencePopup.Duplicate]: 'Duplicate',
    [AudiencePopup.Update]: 'Rename',
    [AudiencePopup.Conditions]: 'Edit',
  };

  submitLabel(): string {
    // Existing audience selected finishes in one step, so the button reflects the direct create
    if (this.isSegmentCreate && this.isExistingAudienceSelected) {
      return 'Create Segment';
    }
    return this.popupTypeTextMap[this.inputData.popupType];
  }
}
